import { Fragment } from "react";
import { ChevronRight, Phone } from "lucide-react";
import { PROCESS_STEPS, BUSINESS } from "#/lib/constants";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { useScrollAnimation } from "#/hooks/useScrollAnimation";

/**
 * Section 5 — "How It Works" as a concise flow diagram (client direction
 * 2026-07-13): 4 flame number nodes joined by dashed arrows, titles only.
 * Step descriptions stay in PROCESS_STEPS for the /process page; the old crew
 * photo + callout layout is gone.
 */
export function Process() {
  const { ref, isVisible } = useScrollAnimation(0.15);

  return (
    <section id="process" className="bg-white py-14 lg:py-20">
      <div ref={ref} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <SectionLabel>How It Works</SectionLabel>
          <h2
            className={`mt-4 font-heading text-3xl font-extrabold leading-[1.1] text-graphite transition-all duration-700 sm:text-4xl ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            From first call to final cure, handled.
          </h2>
        </div>

        {/* Flow diagram.
            Desktop keeps the client-approved horizontal stepper: flame circles,
            dashed connectors, titles only.
            Mobile gets a different pattern entirely, because four numbered
            circles stacked with no connector and no context read as a bare
            list. Instead: a continuous rail down the left with square dark
            tiles (the site's rectangular language), an oversized ghost numeral
            behind each step, and the step description, which already exists in
            PROCESS_STEPS and was going unused on this page. */}
        <ol className="mx-auto mt-10 flex max-w-md flex-col lg:mt-14 lg:max-w-none lg:flex-row lg:items-start lg:justify-center lg:gap-3">
          {PROCESS_STEPS.map((step, i) => (
            <Fragment key={step.number}>
              {i > 0 && (
                <li
                  aria-hidden
                  className="hidden flex-1 items-center pt-7 lg:flex"
                >
                  <span className="h-0 flex-1 border-t-2 border-dashed border-flame/30" />
                  <ChevronRight className="-ml-1.5 h-5 w-5 text-flame/50" />
                </li>
              )}
              <li
                className={`relative flex gap-5 pb-7 transition-all duration-500 last:pb-0 lg:w-48 lg:flex-col lg:gap-0 lg:pb-0 lg:text-center ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                }`}
                style={{ transitionDelay: isVisible ? `${i * 120}ms` : "0ms" }}
              >
                {/* Rail joining the tiles, mobile only */}
                {i < PROCESS_STEPS.length - 1 && (
                  <span
                    aria-hidden
                    className="absolute left-[1.6875rem] top-14 -bottom-1 w-px bg-gradient-to-b from-flame via-flame/40 to-flame/10 lg:hidden"
                  />
                )}

                <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center bg-graphite font-heading text-lg font-black text-flame ring-1 ring-flame/40 lg:mx-auto lg:rounded-full lg:bg-flame lg:text-white lg:ring-4 lg:ring-flame/15">
                  {step.number}
                </span>

                <div className="relative min-w-0 flex-1 pt-1.5 lg:mt-4 lg:pt-0">
                  {/* Oversized ghost numeral, mobile only */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -top-3 right-0 font-heading text-6xl font-black leading-none text-graphite/[0.055] lg:hidden"
                  >
                    {step.number}
                  </span>
                  <h3 className="font-heading relative text-base font-bold uppercase tracking-tight text-graphite">
                    {step.title}
                  </h3>
                  <p className="relative mt-1.5 text-sm leading-relaxed text-concrete-dark lg:hidden">
                    {step.description}
                  </p>
                </div>
              </li>
            </Fragment>
          ))}
        </ol>

        {/* Phone CTA */}
        <div className="mt-10 text-center lg:mt-12">
          <a
            href={BUSINESS.phoneHref}
            className="inline-flex items-center gap-2 font-semibold text-graphite no-underline transition-colors hover:text-flame"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-flame/10 text-flame">
              <Phone className="h-4 w-4" />
            </span>
            Start with a free estimate: {BUSINESS.phone}
          </a>
          <p className="mt-4 text-sm">
            <a
              href="/process"
              className="font-semibold text-flame no-underline transition-colors hover:text-graphite"
            >
              See the full process, step by step
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
